import { useEffect, useMemo, useState } from 'react';
import * as Styled from './styles';
import { Button, Input, Table, Typography, toastify } from '#ui-kit';
import {
  ButtonSize,
  ButtonVariant,
  TableCellAlign,
  TypographySize,
  TypographyTag,
  TypographyWeight,
} from '#ui-kit.types';
import { StatusBadge } from '#components';

import { notAvailableValue } from '#utils';

import { RestaurantsService } from '#services/restaurants';
import { TableProps } from './types/Table.types';
import { RestaurantStatusBadgeMap } from 'pages/Restaurants/constants';
import { RestaurantStatusEnum } from 'types';

const Component: React.FC<TableProps> = ({
  loading,
  data: { data },
  onRestaurantUpdate,
}) => {
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [originalCodes, setOriginalCodes] = useState<Record<string, string>>(
    {},
  );
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const restaurantsService = useMemo(() => new RestaurantsService(), []);

  useEffect(() => {
    setOriginalCodes((prev) => {
      const next = { ...prev };
      data.forEach((r) => {
        next[r.id] = r.code ?? '';
      });
      return next;
    });

    setCodes((prev) => {
      const next = { ...prev };
      data.forEach((r) => {
        if (typeof next[r.id] === 'undefined') {
          next[r.id] = r.code ?? '';
        }
      });
      return next;
    });
  }, [data]);

  const onChangeCode = (id: string, value: string) => {
    setCodes((prev) => ({ ...prev, [id]: value }));
  };

  const onSaveCode = async (id: string) => {
    const current = codes[id] ?? '';
    const baseline = originalCodes[id] ?? '';
    if (current === baseline || saving[id]) return;

    setSaving((prev) => ({ ...prev, [id]: true }));
    try {
      await restaurantsService.updateRestaurant(id, { code: current || null });
      setOriginalCodes((prev) => ({ ...prev, [id]: current }));
      toastify.success('Код обновлен');
    } catch (e) {
      toastify.error('Не удалось обновить код');
    } finally {
      setSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <Table
      {...{
        loading,
        header: {
          cells: {
            0: {
              width: 232,
              align: TableCellAlign.Left,
              children: (
                <Typography size={TypographySize.xs} tag={TypographyTag.span}>
                  Наименование
                </Typography>
              ),
            },
            1: {
              width: 232,
              align: TableCellAlign.Left,
              children: (
                <Typography size={TypographySize.xs} tag={TypographyTag.span}>
                  Адрес
                </Typography>
              ),
            },
            2: {
              width: 232,
              align: TableCellAlign.Left,
              children: (
                <Typography size={TypographySize.xs} tag={TypographyTag.span}>
                  ИНН
                </Typography>
              ),
            },
            3: {
              width: 232,
              align: TableCellAlign.Left,
              children: (
                <Typography size={TypographySize.xs} tag={TypographyTag.span}>
                  Статус
                </Typography>
              ),
            },
            4: {
              width: 320,
              align: TableCellAlign.Left,
              children: (
                <Typography size={TypographySize.xs} tag={TypographyTag.span}>
                  Код
                </Typography>
              ),
            },
            5: {
              width: 180,
            },
          },
        },
        rows: data.map((restaurant) => {
          const value = codes[restaurant.id] ?? restaurant.code ?? '';
          const baseline =
            originalCodes[restaurant.id] ?? restaurant.code ?? '';
          const isChanged = value !== baseline;
          const isSaving = !!saving[restaurant.id];

          return {
            to: `/restaurants/${restaurant.id}`,
            state: { restaurant },
            cells: {
              0: {
                width: 232,
                align: TableCellAlign.Left,
                onClick: () => {},
                children: (
                  <Typography
                    size={TypographySize.s}
                    tag={TypographyTag.span}
                    weight={TypographyWeight.Light}
                  >
                    {notAvailableValue(restaurant.name)}
                  </Typography>
                ),
              },
              1: {
                width: 232,
                align: TableCellAlign.Left,
                onClick: () => {},
                children: (
                  <Typography
                    size={TypographySize.s}
                    tag={TypographyTag.span}
                    weight={TypographyWeight.Light}
                  >
                    {notAvailableValue(restaurant.address)}
                  </Typography>
                ),
              },
              2: {
                width: 232,
                align: TableCellAlign.Left,
                onClick: () => {},
                children: (
                  <Typography
                    size={TypographySize.s}
                    tag={TypographyTag.span}
                    weight={TypographyWeight.Light}
                  >
                    {notAvailableValue(restaurant.tin)}
                  </Typography>
                ),
              },
              3: {
                width: 232,
                align: TableCellAlign.Left,
                onClick: () => {},
                children: (
                  <Styled.RestaurantStatusContainer>
                    <StatusBadge<typeof RestaurantStatusEnum>
                      {...{
                        value: restaurant.status,
                        data: RestaurantStatusBadgeMap,
                      }}
                    />
                  </Styled.RestaurantStatusContainer>
                ),
              },
              4: {
                width: 500,
                align: TableCellAlign.Left,
                children: (
                  <div data-prevent-row-link>
                    <Input
                      name={`code-${restaurant.id}`}
                      value={value}
                      onChange={(v) => onChangeCode(restaurant.id, v)}
                      placeholder="Код"
                      clearable
                    />
                    <Button
                      label="✓"
                      type="button"
                      size={ButtonSize.Small}
                      variant={ButtonVariant.Primary}
                      disabled={!isChanged || isSaving}
                      onClick={() => onSaveCode(restaurant.id)}
                      className="square-button"
                    />
                  </div>
                ),
              },
              5: {
                width: 180,
                align: TableCellAlign.Left,
                children: (
                  <Button
                    {...{
                      label:
                        restaurant.status === RestaurantStatusEnum.archived
                          ? 'Разархивировать'
                          : 'Архивировать',
                      size: ButtonSize.Medium,
                      type: 'button',
                      variant: ButtonVariant.Primary,
                      disabled: loading,
                      onClick: () =>
                        onRestaurantUpdate(
                          restaurant.id,
                          restaurant.status === RestaurantStatusEnum.archived
                            ? RestaurantStatusEnum.open
                            : RestaurantStatusEnum.archived,
                        ),
                    }}
                  />
                ),
              },
            },
          };
        }),
      }}
    />
  );
};

export default Component;
